import React, { useCallback, useMemo, useState } from "react";
import { Card, Flex, Text } from "@jsq/ds/core";
import { Form } from "@jsq/ds/forms";
import { Column, ColumnContainer } from "@jsq/ds/layouts";
import { PayeeStatus, PayeeType } from "#graphql/supergraphEnums";
import { DocumentTracker } from "../../../PayeeWorkflows/components/DocumentTracker";
import { FormStepFooter } from "../../../PayeeWorkflows/components/FormStepFooter";
import { DuplicateInvoiceBanner } from "../../components/DuplicateInvoiceBanner";
import { FormClientSelector } from "../../components/FormClientSelector";
import { FormPayeeSelector } from "../../components/FormPayeeSelector";
import { PayeeCreationModal } from "../../components/PayeeCreationModal";
import { PayeeDisplay } from "../../components/PayeeDisplay";
import { PayeeObjectType } from "../../components/PayeeFilter/types";
import { VendorInvoiceDetails } from "../../components/VendorInvoiceDetails";
import { PayableActionTypes, PayableStepActionTypes } from "../../Context/constants";
import {
  usePayableDataContext,
  usePayableDataDispatch,
  usePayableWorkflowDispatch,
} from "../../Context/PayableContext";
import {
  stepColumnContainerStyles,
  payeeDetailsColumnStyles,
  scrollableColumnStyles,
} from "../../styles_vanilla.css";
import {
  PayableFormFields,
  clientStepValidationSchema,
  type ClientStepFormValues,
} from "../../types";
import { PayableStepProps } from "../types";
import { payeeSectionDividerStyles } from "./styles_vanilla.css";

export const ClientStep: React.FC<PayableStepProps> = () => {
  const payableData = usePayableDataContext();
  const dispatch = usePayableDataDispatch();
  const stepDispatch = usePayableWorkflowDispatch();

  // Allow for initial Payee display. Allows for going back and forth between steps and starting with initial
  // values as well if we ever want to reuse for Edit flow.
  const initialPayee = useMemo((): PayeeObjectType | null => {
    if (payableData.payeeId && payableData.payeeName && payableData.payeeType) {
      return {
        id: payableData.payeeId,
        name: payableData.payeeName,
        payeeType: payableData.payeeType,
        status: payableData.payeeStatus ?? PayeeStatus.Pending,
        paymentPref: {
          id: "",
          bankInfoRedacted: null,
          watchlistStatus: [],
        },
      };
    }
    return null;
  }, [
    payableData.payeeId,
    payableData.payeeName,
    payableData.payeeType,
    payableData.payeeStatus,
  ]);

  const [selectedPayee, setSelectedPayee] = useState<PayeeObjectType | null>(initialPayee);
  const [selectedArenaName, setSelectedArenaName] = useState<string>(
    payableData.arenaName ?? "",
  );
  const [selectedArenaId, setSelectedArenaId] = useState<string>(payableData.arenaId ?? "");
  const [isPayeeCreationModalOpen, setIsPayeeCreationModalOpen] = useState(false);

  const handleClientSelect = useCallback(
    (arenaId: string, arenaName: string) => {
      setSelectedArenaId(arenaId);
      setSelectedArenaName(arenaName);
      setSelectedPayee(null);
      dispatch({
        type: PayableActionTypes.SET_CLIENT,
        payload: { arenaId, arenaName },
      });
    },
    [dispatch],
  );

  const handlePayeeSelect = useCallback(
    (payee?: PayeeObjectType | null) => {
      setSelectedPayee(payee ?? null);
      if (payee) {
        dispatch({
          type: PayableActionTypes.SET_PAYEE,
          payload: {
            payeeId: payee.id,
            payeeName: payee.name,
            payeeType: payee.payeeType,
            payeeStatus: payee.status,
          },
        });
      }
    },
    [dispatch],
  );

  const isVendorPayee = selectedPayee?.payeeType === PayeeType.Vendor;

  const handleFormSubmit = useCallback(
    (values: Partial<ClientStepFormValues>) => {
      // Dispatch invoice details for vendor payees (no invoiceAmount - collected in Step 2)
      if (isVendorPayee) {
        dispatch({
          type: PayableActionTypes.SET_INVOICE_DETAILS,
          payload: {
            invoiceNumber: values.invoiceNumber ?? "",
            invoiceDate: values.invoiceDate ?? null,
            dueDate: values.dueDate ?? null,
            description: values.description ?? "",
          },
        });
      }
      // Advance to next step
      stepDispatch({ type: PayableStepActionTypes.INCREMENT_STEP });
    },
    [dispatch, isVendorPayee, stepDispatch],
  );

  const firstDocument = payableData.invoiceDocuments?.[0];

  const initialValues = {
    [PayableFormFields.ArenaId]: selectedArenaId,
    [PayableFormFields.PayeeId]: selectedPayee?.id ?? "",
    [PayableFormFields.PayeeType]: selectedPayee?.payeeType ?? "",
    // Invoice details (vendor only)
    [PayableFormFields.InvoiceNumber]: payableData.invoiceNumber ?? "",
    [PayableFormFields.InvoiceDate]: payableData.invoiceDate ?? null,
    [PayableFormFields.DueDate]: payableData.dueDate ?? null,
    [PayableFormFields.Description]: payableData.description ?? "",
  };

  return (
    <>
      <Form
        yupValidationSchema={clientStepValidationSchema}
        initialValues={initialValues}
        enableReinitialize={true}
        onSubmit={handleFormSubmit}
        submitOnlyChangedValues={false}
      >
        <ColumnContainer className={stepColumnContainerStyles}>
          {/* Left Column - Document Display */}
          <Column size="fluid">
            {firstDocument && (
              <DocumentTracker
                documentName={firstDocument.name}
                documentUrl={firstDocument.localUrl}
              />
            )}
          </Column>

          {/* Right Column - Form */}
          <Column
            size="fluid"
            className={`${payeeDetailsColumnStyles} ${scrollableColumnStyles}`}
          >
            <Card title="Select client">
              <FormClientSelector
                selectedArenaName={selectedArenaName}
                onClientSelect={handleClientSelect}
                fieldName={PayableFormFields.ArenaId}
                placeholder="Select a client for this payable"
              />
              <Flex flexDirection="column" gap={16} className={payeeSectionDividerStyles}>
                <Text variant="h3" style={{ margin: 0 }}>
                  Select payee
                </Text>
                <FormPayeeSelector
                  onPayeeSelect={handlePayeeSelect}
                  fieldName={PayableFormFields.PayeeId}
                  arenaId={selectedArenaId}
                  disabled={!selectedArenaId}
                  onCreateNewPayee={() => setIsPayeeCreationModalOpen(true)}
                  selectedPayee={selectedPayee}
                />
                {selectedPayee && (
                  <PayeeDisplay payeeId={selectedPayee.id} showDiligenceStatus />
                )}
              </Flex>
            </Card>

            {/* Invoice Details Card - only visible for vendor payees */}
            {isVendorPayee && (
              <>
                <DuplicateInvoiceBanner />
                <Card title="Invoice details">
                  <VendorInvoiceDetails />
                </Card>
              </>
            )}

            <FormStepFooter submitText="Continue" />
          </Column>
        </ColumnContainer>
      </Form>

      <PayeeCreationModal
        isOpen={isPayeeCreationModalOpen}
        onClose={() => setIsPayeeCreationModalOpen(false)}
        onPayeeCreated={(payee?: PayeeObjectType | null) => {
          if (payee) {
            handlePayeeSelect(payee);
          }
          setIsPayeeCreationModalOpen(false);
        }}
        arenaId={selectedArenaId}
        arenaName={selectedArenaName}
        initialDocument={firstDocument}
      />
    </>
  );
};

export default ClientStep;
